import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Handshake } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPERIENCE_OPTIONS = ["Fresher", "0-1 Years", "1-3 Years", "3+ Years"];
const INDUSTRY_OPTIONS = [
  "Engineering", "Manufacturing", "Hospitality", "Logistics",
  "Retail", "IT", "Healthcare", "Other",
];

const schema = z.object({
  full_name: z.string().min(2, "Please enter your full name"),
  mobile: z.string().min(10, "Enter a valid 10-digit mobile").max(15),
  city: z.string().min(2, "Please enter your city"),
  experience: z.string().min(1, "Please select your experience"),
});

export default function PartnerApplicationDialog({ open, onClose }) {
  const [industries, setIndustries] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const {
    register, handleSubmit, reset, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { full_name: "", mobile: "", city: "", experience: "" },
  });

  const toggleIndustry = (ind) => {
    setIndustries((prev) => prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind]);
  };

  const onSubmit = async (data) => {
    // Reuse existing /api/leads endpoint. Generate synthetic email since Lead schema requires it.
    const cleanMobile = data.mobile.replace(/\D/g, "");
    const industriesText = industries.length ? industries.join(", ") : "Not specified";

    const payload = {
      full_name: data.full_name.trim(),
      email: `partner-${cleanMobile}@partner.advancehrtech.com`,
      phone: data.mobile.trim(),
      company: data.city.trim(),
      service_interest: "Recruitment Partner",
      employees_needed: data.experience,
      message: `Recruitment Partner Application\nCity: ${data.city}\nExperience: ${data.experience}\nIndustries: ${industriesText}`,
      source: "recruitment_partner",
    };

    try {
      await axios.post(`${API}/leads`, payload);
      setSubmitted(true);
    } catch (e) {
      toast.error("Unable to submit right now. Please try again.");
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    setIndustries([]);
    setSubmitted(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        data-testid="partner-application-dialog"
        className="max-w-lg max-h-[92vh] overflow-y-auto"
      >
        {!submitted ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <Handshake className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div>
                  <DialogTitle className="font-heading font-bold text-xl text-slate-900">
                    Apply as Recruitment Partner
                  </DialogTitle>
                  <DialogDescription className="text-xs text-slate-500 mt-0.5">
                    Takes less than a minute. We&apos;ll reach out shortly.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4" data-testid="partner-application-form">
              <div>
                <Label htmlFor="full_name" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Full Name *
                </Label>
                <Input
                  id="full_name"
                  data-testid="partner-full-name"
                  {...register("full_name")}
                  placeholder="e.g. Rahul Sharma"
                  className="mt-1.5"
                />
                {errors.full_name && (
                  <p role="alert" className="mt-1 text-xs text-red-600">{errors.full_name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="mobile" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Mobile Number *
                </Label>
                <Input
                  id="mobile"
                  data-testid="partner-mobile"
                  {...register("mobile")}
                  placeholder="98765 43210"
                  inputMode="tel"
                  className="mt-1.5"
                />
                {errors.mobile && (
                  <p role="alert" className="mt-1 text-xs text-red-600">{errors.mobile.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="city" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  City *
                </Label>
                <Input
                  id="city"
                  data-testid="partner-city"
                  {...register("city")}
                  placeholder="e.g. Mumbai"
                  className="mt-1.5"
                />
                {errors.city && (
                  <p role="alert" className="mt-1 text-xs text-red-600">{errors.city.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="experience" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Recruitment Experience *
                </Label>
                <select
                  id="experience"
                  data-testid="partner-experience"
                  {...register("experience")}
                  className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                >
                  <option value="">Select experience</option>
                  {EXPERIENCE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
                {errors.experience && (
                  <p role="alert" className="mt-1 text-xs text-red-600">{errors.experience.message}</p>
                )}
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Industries You Recruit For
                  <span className="ml-1 normal-case font-normal text-[10px] text-slate-400">(optional)</span>
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {INDUSTRY_OPTIONS.map((ind) => {
                    const active = industries.includes(ind);
                    return (
                      <button
                        key={ind}
                        type="button"
                        data-testid={`partner-industry-${ind.toLowerCase()}`}
                        onClick={() => toggleIndustry(ind)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          active
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-slate-700 border-slate-200 hover:border-primary/40"
                        }`}
                      >
                        {ind}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Button
                type="submit"
                data-testid="partner-submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white font-semibold h-11"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                ) : (
                  <>Apply as Recruitment Partner</>
                )}
              </Button>

              <p className="text-[11px] text-slate-500 text-center">
                By submitting, you agree to be contacted by our team.
              </p>
            </form>
          </>
        ) : (
          <div data-testid="partner-application-success" className="py-4 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" strokeWidth={1.75} />
            </div>
            <h3 className="mt-4 font-heading font-bold text-2xl text-slate-900">🎉 Thank You!</h3>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              Your Recruitment Partner application has been received successfully.
              Our team will review your details and contact you shortly.
            </p>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              If you are an existing Recruitment Partner, you can log in using the Recruitment Partner Login.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/login"
                onClick={handleClose}
                data-testid="partner-success-login-cta"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors"
              >
                <Handshake className="w-4 h-4" /> Recruitment Partner Login
              </Link>
              <button
                type="button"
                onClick={handleClose}
                data-testid="partner-success-close"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-white text-slate-700 font-semibold text-sm border border-slate-200 hover:border-slate-300"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
